/**
 * Advanced Algolia Search for Shopify + Zendesk
 * This file provides enhanced search functionality across multiple indexes
 */

class AlgoliaMultiSearch {
  constructor(config = {}) {
    // Configuration
    this.config = {
      appId: config.appId || '3MMVVHL446',
      apiKey: config.apiKey || '359405eb9d9a709c410b5fc9c1e80ee8',
      indexes: config.indexes || {
        products: 'prod_shopify_products',
        articles: 'prod_shopify_articles',
        pages: 'prod_shopify_pages',
        help: 'zendesk_ekodevices_articles'
      },
      selectors: config.selectors || {
        searchContainer: '#algoliaSearch',
        searchInput: '#algoliaSearchInput',
        searchButton: '#algoliaSearchButton',
        clearButton: '#algoliaSearchClear',
        resultsContainer: '#algoliaSearchResults',
        resultsContent: '#algoliaResultsContent',
        productsSlider: '#algoliaProductsSlider',
        productsSection: '#algoliaProductsSection',
        contentSection: '#algoliaContentSection',
        noResults: '#algoliaNoResults',
        loader: '#algoliaLoader',
        tabs: '.algolia-tab'
      },
      hitsPerPage: config.hitsPerPage || 20,
      debounceTime: config.debounceTime || 300,
      templates: config.templates || {}
    };

    // State
    this.state = {
      activeTab: 'articles_pages',
      searchTimeout: null,
      cachedResults: {
        products: [],
        articles: [],
        pages: [],
        help: []
      },
      totalResults: 0
    };

    // Initialize Algolia client and indexes
    this.client = algoliasearch(this.config.appId, this.config.apiKey);
    this.indexes = {
      products: this.client.initIndex(this.config.indexes.products),
      articles: this.client.initIndex(this.config.indexes.articles),
      pages: this.client.initIndex(this.config.indexes.pages),
      help: this.client.initIndex(this.config.indexes.help)
    };

    // Cache DOM elements
    this.elements = {
      searchContainer: document.querySelector(this.config.selectors.searchContainer),
      input: document.querySelector(this.config.selectors.searchInput),
      searchButton: document.querySelector(this.config.selectors.searchButton),
      clearButton: document.querySelector(this.config.selectors.clearButton),
      resultsContainer: document.querySelector(this.config.selectors.resultsContainer),
      resultsContent: document.querySelector(this.config.selectors.resultsContent),
      productsSlider: document.querySelector(this.config.selectors.productsSlider),
      productsSection: document.querySelector(this.config.selectors.productsSection),
      contentSection: document.querySelector(this.config.selectors.contentSection),
      noResults: document.querySelector(this.config.selectors.noResults),
      loader: document.querySelector(this.config.selectors.loader),
      tabs: document.querySelectorAll(this.config.selectors.tabs),
      counts: {
        articlesContent: document.getElementById('articlesContentCount'),
        help: document.getElementById('helpCount')
      }
    };

    // Initialize if all required elements exist
    if (this.elements.searchContainer && this.elements.input) {
      this.init();
    } else {
      console.error('Algolia Multi Search: Required DOM elements not found');
    }
  }

  /**
   * Initialize the search functionality
   */
  init() {
    // Search input events
    this.elements.input.addEventListener('input', this.handleSearchInput.bind(this));
    this.elements.input.addEventListener('focus', () => {
      if (this.elements.input.value.trim()) {
        this.elements.resultsContainer.classList.remove('hidden');
      }
    });
    
    // Search/clear buttons
    this.elements.searchButton.addEventListener('click', () => {
      this.elements.input.focus();
      if (this.elements.input.value.trim()) {
        this.performSearch(this.elements.input.value);
      }
    });
    
    this.elements.clearButton.addEventListener('click', this.clearSearch.bind(this));
    
    // Tab switching
    this.elements.tabs.forEach(tab => {
      tab.addEventListener('click', () => {
        const index = tab.getAttribute('data-index');
        this.switchTab(index);
      });
    });
    
    // Close search when clicking outside
    document.addEventListener('click', (e) => {
      if (!e.target.closest(this.config.selectors.searchContainer) && !e.target.closest('.header__icon--search')) {
        this.elements.resultsContainer.classList.add('hidden');
      }
    });
    
    // Submit on enter
    this.elements.input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && this.elements.input.value.trim()) {
        this.performSearch(this.elements.input.value);
      }
    });

    // Slider navigation if available
    const sliderPrev = document.getElementById('algoliaSliderPrev');
    const sliderNext = document.getElementById('algoliaSliderNext');
    
    if (sliderPrev && sliderNext && this.elements.productsSlider) {
      sliderPrev.addEventListener('click', () => {
        this.elements.productsSlider.scrollBy({ left: -300, behavior: 'smooth' });
      });
      
      sliderNext.addEventListener('click', () => {
        this.elements.productsSlider.scrollBy({ left: 300, behavior: 'smooth' });
      });
    }

    // Track analytics if needed
    if (typeof this.config.onInit === 'function') {
      this.config.onInit(this);
    }

    // Initialize query parameter search if present
    this.checkQueryParameters();
  }

  /**
   * Check URL for search query parameters
   */
  checkQueryParameters() {
    const urlParams = new URLSearchParams(window.location.search);
    const searchQuery = urlParams.get('q');
    
    if (searchQuery) {
      this.elements.input.value = searchQuery;
      this.performSearch(searchQuery);
    }
  }

  /**
   * Handle search input with debounce
   */
  handleSearchInput() {
    const query = this.elements.input.value.trim();
    
    if (query) {
      this.elements.clearButton.classList.remove('hidden');
      
      clearTimeout(this.state.searchTimeout);
      this.state.searchTimeout = setTimeout(() => {
        this.performSearch(query);
      }, this.config.debounceTime);
    } else {
      this.elements.clearButton.classList.add('hidden');
      this.elements.resultsContainer.classList.add('hidden');
      this.clearResults();
    }
  }

  /**
   * Clear search
   */
  clearSearch() {
    this.elements.input.value = '';
    this.elements.clearButton.classList.add('hidden');
    this.elements.resultsContainer.classList.add('hidden');
    this.clearResults();
  }

  /**
   * Clear results
   */
  clearResults() {
    if (this.elements.resultsContent) {
      this.elements.resultsContent.innerHTML = '';
    }
    
    if (this.elements.productsSlider) {
      this.elements.productsSlider.innerHTML = '';
    }
    
    if (this.elements.counts.articlesContent) {
      this.elements.counts.articlesContent.textContent = '0';
    }
    
    if (this.elements.counts.help) {
      this.elements.counts.help.textContent = '0';
    }
    
    if (this.elements.productsSection) {
      this.elements.productsSection.classList.add('hidden');
    }
    
    if (this.elements.contentSection) {
      this.elements.contentSection.classList.add('hidden');
    }
    
    this.state.cachedResults = {
      products: [],
      articles: [],
      pages: [],
      help: []
    };
    
    this.state.totalResults = 0;
  }

  /**
   * Show loader
   */
  showLoader() {
    if (this.elements.loader) {
      this.elements.loader.classList.remove('hidden');
    }
  }

  /**
   * Hide loader
   */
  hideLoader() {
    if (this.elements.loader) {
      this.elements.loader.classList.add('hidden');
    }
  }

  /**
   * Perform search across all indexes
   * @param {string} query - Search query
   */
  async performSearch(query) {
    if (!query) return;
    
    this.showLoader();
    this.clearResults();
    
    try {
      // Search all indexes in parallel
      const [productsResults, articlesResults, pagesResults, helpResults] = await Promise.all([
        this.indexes.products.search(query, { hitsPerPage: this.config.hitsPerPage }),
        this.indexes.articles.search(query, { hitsPerPage: this.config.hitsPerPage }),
        this.indexes.pages.search(query, { hitsPerPage: this.config.hitsPerPage }),
        this.indexes.help.search(query, { hitsPerPage: this.config.hitsPerPage })
      ]);
      
      // Cache results
      this.state.cachedResults.products = productsResults.hits;
      this.state.cachedResults.articles = articlesResults.hits;
      this.state.cachedResults.pages = pagesResults.hits;
      this.state.cachedResults.help = helpResults.hits;
      
      // Calculate total results
      const articlesContentCount = articlesResults.hits.length + pagesResults.hits.length;
      this.state.totalResults = productsResults.hits.length + articlesContentCount + helpResults.hits.length;
      
      // Update counts
      if (this.elements.counts.articlesContent) {
        this.elements.counts.articlesContent.textContent = articlesContentCount;
      }
      
      if (this.elements.counts.help) {
        this.elements.counts.help.textContent = helpResults.hits.length;
      }
      
      // Update section visibility
      if (this.elements.productsSection) {
        this.elements.productsSection.classList.toggle('hidden', productsResults.hits.length === 0);
      }
      
      if (this.elements.contentSection) {
        this.elements.contentSection.classList.toggle('hidden', articlesContentCount === 0 && helpResults.hits.length === 0);
      }
      
      // Hide tabs with no results
      const articlesContentTab = document.getElementById('articlesContentTab');
      const helpCenterTab = document.getElementById('helpCenterTab');
      
      if (articlesContentTab) {
        articlesContentTab.classList.toggle('hidden', articlesContentCount === 0);
      }
      
      if (helpCenterTab) {
        helpCenterTab.classList.toggle('hidden', helpResults.hits.length === 0);
      }
      
      // If current tab has no results but the other tab does, switch to the other tab
      if (this.state.activeTab === 'articles_pages' && articlesContentCount === 0 && helpResults.hits.length > 0) {
        this.switchTab('zendesk_ekodevices_articles');
      } else if (this.state.activeTab === 'zendesk_ekodevices_articles' && helpResults.hits.length === 0 && articlesContentCount > 0) {
        this.switchTab('articles_pages');
      }
      
      // Display products in slider if available
      if (this.elements.productsSlider) {
        this.displayProductsSlider();
      }
      
      // Display content results
      this.displayContentResults();
      
      // Show no results message if needed
      if (this.elements.noResults) {
        this.elements.noResults.classList.toggle('hidden', this.state.totalResults > 0);
      }
      
      // Show results container
      if (this.elements.resultsContainer) {
        this.elements.resultsContainer.classList.remove('hidden');
      }

      // Track analytics if needed
      if (typeof this.config.onSearch === 'function') {
        this.config.onSearch({
          query,
          results: {
            products: productsResults,
            articles: articlesResults,
            pages: pagesResults,
            help: helpResults
          },
          totalResults: this.state.totalResults
        });
      }
    } catch (error) {
      console.error('Algolia search error:', error);
      
      // Error handling callback
      if (typeof this.config.onError === 'function') {
        this.config.onError(error);
      }
    } finally {
      this.hideLoader();
    }
  }

  /**
   * Switch between tabs
   * @param {string} tabName - Name of the tab to switch to
   */
  switchTab(tabName) {
    this.state.activeTab = tabName;
    
    // Update tab classes
    this.elements.tabs.forEach(tab => {
      if (tab.getAttribute('data-index') === tabName) {
        tab.classList.add('active');
      } else {
        tab.classList.remove('active');
      }
    });
    
    // Update displayed results
    this.displayContentResults();

    // Track tab change if needed
    if (typeof this.config.onTabChange === 'function') {
      this.config.onTabChange(tabName);
    }
  }

  /**
   * Display products in slider
   */
  displayProductsSlider() {
    if (!this.elements.productsSlider) return;
    
    this.elements.productsSlider.innerHTML = '';
    
    if (this.state.cachedResults.products.length === 0) {
      return;
    }
    
    this.state.cachedResults.products.forEach(product => {
      const card = document.createElement('a');
      card.className = 'algolia-product-card';
      card.href = '/products/' + (product.handle || '');
      
      const image = document.createElement('img');
      image.className = 'algolia-product-image bg-beige-50 rounded-lg mb-xs';
      image.src = product.image || '/assets/no-image.png';
      image.alt = product.title || product.name || '';
      
      const title = document.createElement('div');
      title.className = 'algolia-product-title';
      title.textContent = product.title || product.name || 'Untitled Product';
      
      card.appendChild(image);
      card.appendChild(title);
      
      this.elements.productsSlider.appendChild(card);
    });
  }

  /**
   * Display results based on active tab
   */
  displayContentResults() {
    if (!this.elements.resultsContent) return;
    
    this.elements.resultsContent.innerHTML = '';
    
    if (this.state.totalResults === 0) {
      if (this.elements.noResults) {
        this.elements.noResults.classList.remove('hidden');
      }
      return;
    }
    
    if (this.elements.noResults) {
      this.elements.noResults.classList.add('hidden');
    }
    
    let results = [];
    
    if (this.state.activeTab === 'articles_pages') {
      // Display articles and pages combined
      results = [
        ...this.state.cachedResults.articles.map(hit => ({ ...hit, type: 'article' })),
        ...this.state.cachedResults.pages.map(hit => ({ ...hit, type: 'page' }))
      ];
    } else if (this.state.activeTab === 'zendesk_ekodevices_articles') {
      // Display help center results
      results = this.state.cachedResults.help.map(hit => ({ ...hit, type: 'help' }));
    }
    
    // Render results
    results.forEach(result => {
      this.elements.resultsContent.appendChild(this.createResultElement(result));
    });
  }

  /**
   * Create result element based on type
   * @param {Object} result - Result object from Algolia
   * @returns {HTMLElement} - Result element
   */
  createResultElement(result) {
    // Use custom template if provided
    if (this.config.templates[result.type]) {
      return this.config.templates[result.type](result);
    }
    
    const item = document.createElement('a');
    item.className = 'algolia-result-item pt-sm pb-sm';
    
    // Set URL based on result type
    switch (result.type) {
      case 'product':
        item.href = '/products/' + (result.handle || '');
        break;
      case 'article':
        if (result.blog && result.handle) {
          item.href = '/blogs/' + result.blog.handle + '/' + result.handle;
        }
        break;
      case 'page':
        item.href = '/pages/' + (result.handle || '');
        break;
      case 'help':
        // Zendesk URLs often have a specific format
        item.href = result.url || ('https://support.ekohealth.com/hc/en-us/articles/' + result.id);
        item.target = '_blank'; // Open help center links in a new tab
        break;
    }
    
    // Set image
    const image = document.createElement('img');
    image.className = 'algolia-result-image';
    
    // Set image source based on result type
    if (result.type === 'product' && result.image) {
      image.src = result.image;
    } else if (result.type === 'article' && result.image) {
      image.src = result.image;
    } else {
      if (result.type === 'help') {
        image.style.display = 'none'; // Hide image for help articles
      }
    }
    
    // Create content div
    const content = document.createElement('div');
    content.className = 'algolia-result-content';
    
    // Add title
    const title = document.createElement('div');
    title.className = 'algolia-result-title';
    title.textContent = result.title || result.name || 'Untitled';
    content.appendChild(title);
    
    // Add description if available
    if (result.description || result.body || result.content) {
      const description = document.createElement('div');
      description.className = 'algolia-result-description';
      description.textContent = result.description || result.body || result.content || '';
      
      // Truncate long descriptions
      if (description.textContent.length > 150) {
        description.textContent = description.textContent.substring(0, 147) + '...';
      }
      
      content.appendChild(description);
    }
    
    // Assemble the result item
    item.appendChild(image);
    item.appendChild(content);
    
    return item;
  }
  
}

// Global export
window.AlgoliaMultiSearch = AlgoliaMultiSearch;